import { useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import clsx from 'clsx';
import { salesApi } from '../../api';
import { PageHeader, LoadingState, ErrorState, EmptyState, StatusBadge } from '../../components/common';
import { getErrorMessage } from '../../api/client';
import { fmtCurrency, fmtMoney } from '../../utils/currency';

import { Modal } from '../../components/Modal';

export function CustomersPage() {
  const qc = useQueryClient();
  const [q, setQ] = useState('');
  const [typeFilter, setTypeFilter] = useState<'all'|'individual'|'business'>('all');
  const [showCreate, setShowCreate] = useState(false);
  const [editing, setEditing] = useState<any | null>(null);

  const { data, isLoading, error } = useQuery({
    queryKey: ['customers', q],
    queryFn: () => salesApi.listCustomers({ q: q || undefined, limit: 200 }),
  });

  const createMut = useMutation({
    mutationFn: (p: any) => salesApi.createCustomer(p),
    onSuccess: () => { toast.success('Customer created'); qc.invalidateQueries({ queryKey: ['customers'] }); setShowCreate(false); },
    onError: (e) => toast.error(getErrorMessage(e)),
  });
  const updateMut = useMutation({
    mutationFn: ({ id, payload }: any) => salesApi.updateCustomer(id, payload),
    onSuccess: () => { toast.success('Customer updated'); qc.invalidateQueries({ queryKey: ['customers'] }); setEditing(null); },
    onError: (e) => toast.error(getErrorMessage(e)),
  });
  const toggleMut = useMutation({
    mutationFn: ({ id, status }: any) => salesApi.updateCustomer(id, { status }),
    onSuccess: (_d, v) => { toast.success(v.status === 'active' ? 'Customer activated' : 'Customer deactivated'); qc.invalidateQueries({ queryKey: ['customers'] }); },
    onError: (e) => toast.error(getErrorMessage(e)),
  });
  const deleteMut = useMutation({
    mutationFn: (id: string) => salesApi.deleteCustomer(id),
    onSuccess: (r: any) => { toast.success(r?.message || 'Customer deleted'); qc.invalidateQueries({ queryKey: ['customers'] }); },
    onError: (e) => toast.error(getErrorMessage(e)),
  });

  const { kpis, filteredRows } = useMemo(() => {
    const rows = data ?? [];
    const kpis = {
      total: rows.length,
      active: rows.filter((c: any) => c.status === 'active').length,
      individual: rows.filter((c: any) => c.type === 'individual').length,
      business: rows.filter((c: any) => c.type === 'business').length,
    };
    const filtered = rows.filter((c: any) => typeFilter === 'all' || c.type === typeFilter);
    return { kpis, filteredRows: filtered };
  }, [data, typeFilter]);

  return (
    <div className="px-8 py-10 max-w-7xl mx-auto">
      <PageHeader eyebrow="Commerce" title="Customers"
        subtitle="Your customer base — contacts, credit, and purchase history."
        actions={
          <>
            <button className="btn-primary" onClick={() => setShowCreate(true)}><PlusIcon /> New customer</button>
          </>
        }
      />

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        <KpiTile label="Total customers" value={kpis.total} />
        <KpiTile label="Active" value={kpis.active} accent="sage" />
        <KpiTile label="Individual" value={kpis.individual} />
        <KpiTile label="Business" value={kpis.business} />
      </div>

      <div className="card p-1.5 mb-4 flex gap-2 items-center flex-wrap">
        <div className="flex-1 min-w-48 flex items-center gap-2 px-3">
          <SearchIcon />
          <input type="text" placeholder="Search by name, email, or phone…" value={q}
            onChange={(e) => setQ(e.target.value)}
            className="flex-1 bg-transparent border-0 outline-none text-sm placeholder:text-ink-400 py-2 min-w-0" />
        </div>
        <div className="flex gap-1 bg-canvas-subtle rounded-pill p-1">
          {(['all','individual','business'] as const).map((k) => (
            <button key={k} onClick={() => setTypeFilter(k)}
              className={clsx('px-3 py-1 text-xs font-semibold rounded-pill transition-all capitalize',
                typeFilter === k ? 'bg-canvas-elevated text-ink-900 shadow-xs' : 'text-ink-600 hover:text-ink-900')}>
              {k === 'all' ? 'All' : k}
            </button>
          ))}
        </div>
      </div>

      {isLoading ? <div className="card"><LoadingState rows={6} /></div>
        : error ? <ErrorState error={error} />
        : !filteredRows.length ? (
          <div className="card">
            <EmptyState message={q ? 'No customers match' : 'No customers yet'}
              hint={q ? 'Try a different keyword.' : 'Add your first customer to start tracking orders.'}
              action={!q && <button className="btn-primary" onClick={() => setShowCreate(true)}><PlusIcon /> New customer</button>}
            />
          </div>
        ) : (
          <div className="card overflow-hidden">
            <div className="overflow-x-auto">
              <table className="table">
                <thead>
                  <tr>
                    <th>Customer</th><th>Type</th><th>Contact</th><th>Tier</th>
                    <th className="text-right">Credit limit</th><th>Status</th>
                    <th className="text-right w-44">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredRows.map((c: any) => (
                    <tr key={c.id}>
                      <td>
                        <Link to={`/sales/customers/${c.id}`} className="flex items-center gap-3 group">
                          <CustomerAvatar name={c.name} type={c.type} />
                          <div className="min-w-0">
                            <div className="font-semibold text-ink-900 group-hover:text-sage-700 transition-colors truncate">{c.name}</div>
                            <div className="font-mono text-xs text-ink-500">{c.code}</div>
                          </div>
                        </Link>
                      </td>
                      <td><span className="badge bg-canvas-subtle text-ink-700 ring-1 ring-inset ring-ink-200 capitalize">{c.type}</span></td>
                      <td className="text-sm text-ink-600">
                        {c.email && <div className="truncate">{c.email}</div>}
                        {c.phone && <div className="text-xs text-ink-500">{c.phone}</div>}
                        {!c.email && !c.phone && <span className="text-ink-400">—</span>}
                      </td>
                      <td className="text-sm text-ink-700">
                        {c.tier_name ? <span className="inline-flex items-center gap-1 text-sage-700 font-medium"><span>★</span>{c.tier_name}</span> : '—'}
                      </td>
                      <td className="text-right font-mono font-semibold text-ink-900 tabular-nums">{fmtCurrency(parseFloat(c.credit_limit))}</td>
                      <td><StatusBadge status={c.status} /></td>
                      <td className="text-right">
                        <div className="flex items-center justify-end gap-1">
                          <button className="text-xs font-medium px-2 py-1 rounded hover:bg-canvas-subtle transition-colors text-ink-600"
                            onClick={() => setEditing(c)}>Edit</button>
                          <button
                            className={`text-xs font-medium px-2 py-1 rounded transition-colors ${c.status === 'active' ? 'text-ink-500 hover:bg-canvas-subtle' : 'text-sage-700 hover:bg-sage-50'}`}
                            disabled={toggleMut.isPending}
                            onClick={() => toggleMut.mutate({ id: c.id, status: c.status === 'active' ? 'inactive' : 'active' })}>
                            {c.status === 'active' ? 'Deactivate' : 'Activate'}
                          </button>
                          <button
                            className="text-xs font-medium px-2 py-1 rounded transition-colors text-red-600 hover:bg-red-50"
                            disabled={deleteMut.isPending}
                            onClick={() => { if (window.confirm(`Delete customer "${c.name}"? If it has linked records it will be deactivated instead.`)) deleteMut.mutate(c.id); }}>
                            Delete
                          </button>
                          <Link to={`/sales/customers/${c.id}`} className="text-ink-400 hover:text-ink-700 px-1"><ChevronIcon /></Link>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

      {showCreate && <CustomerModal onSubmit={(p) => createMut.mutate(p)} onClose={() => setShowCreate(false)} loading={createMut.isPending} />}
      {editing && <CustomerModal initial={editing} onSubmit={(p) => updateMut.mutate({ id: editing.id, payload: p })} onClose={() => setEditing(null)} loading={updateMut.isPending} />}
    </div>
  );
}

function CustomerAvatar({ name, type }: { name: string; type: string }) {
  const initials = name.split(/\s+/).filter(Boolean).slice(0, 2).map((s) => s[0]?.toUpperCase() ?? '').join('') || '?';
  const hash = [...name].reduce((a, c) => a + c.charCodeAt(0), 0);
  const tones = ['bg-sage-100 text-sage-700','bg-amber-100 text-amber-800','bg-blue-100 text-blue-700','bg-purple-100 text-purple-700','bg-rose-100 text-rose-700','bg-teal-100 text-teal-700'];
  return <div className={clsx('w-9 h-9 flex items-center justify-center text-xs font-semibold flex-shrink-0', tones[hash % tones.length], type === 'business' ? 'rounded-md' : 'rounded-full')}>{initials}</div>;
}

function CustomerModal({ initial, onSubmit, onClose, loading }: {
  initial?: any; onSubmit: (p: any) => void; onClose: () => void; loading: boolean;
}) {
  const isEdit = !!initial;
  const [form, setForm] = useState({
    code: initial?.code ?? '',
    name: initial?.name ?? '',
    type: initial?.type ?? 'individual',
    email: initial?.email ?? '',
    phone: initial?.phone ?? '',
    credit_limit: initial?.credit_limit ?? 0,
  });
  return (
    <Modal
      onClose={onClose}
      overlayClassName="fixed inset-0 bg-ink-900/40 backdrop-blur-sm flex items-center justify-center z-50 p-4"
      className="bg-canvas-elevated rounded-2xl shadow-xl max-w-md w-full overflow-hidden">
      <div className="px-6 pt-6 pb-2">
        <div className="eyebrow mb-1">Commerce</div>
        <h2 className="text-xl font-bold font-display text-ink-900">{isEdit ? 'Edit customer' : 'New customer'}</h2>
        {!isEdit && <p className="text-sm text-ink-500 mt-1">Add a new customer. Addresses and loyalty tier can be set later.</p>}
      </div>
      <div className="px-6 py-5 space-y-4">
        {!isEdit && (
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="label">Customer code <span className="text-danger">*</span></label>
              <input className="input font-mono" value={form.code} onChange={(e) => setForm({ ...form, code: e.target.value })} placeholder="CUST-001" />
            </div>
            <div>
              <label className="label">Type</label>
              <select className="input" value={form.type} onChange={(e) => setForm({ ...form, type: e.target.value })}>
                <option value="individual">Individual</option>
                <option value="business">Business</option>
              </select>
            </div>
          </div>
        )}
        <div>
          <label className="label">Name <span className="text-danger">*</span></label>
          <input className="input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
            placeholder={form.type === 'business' ? 'Acme Inc.' : 'Jane Doe'} />
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="label">Email</label>
            <input type="email" className="input" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder="customer@example.com" />
          </div>
          <div>
            <label className="label">Phone</label>
            <input className="input" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} placeholder="+1 555 123 4567" />
          </div>
        </div>
        <div>
          <label className="label">Credit limit</label>
          <div className="relative">
            <span className="absolute left-3.5 top-1/2 -translate-y-1/2 text-ink-500 text-sm">$</span>
            <input type="number" step="0.01" className="input pl-7 font-mono tabular-nums"
              value={form.credit_limit} onChange={(e) => setForm({ ...form, credit_limit: parseFloat(e.target.value) || 0 })} />
          </div>
          <p className="text-xs text-ink-500 mt-1.5">Max outstanding balance. Zero = cash-only.</p>
        </div>
      </div>
      <div className="px-6 py-4 bg-canvas-subtle border-t border-ink-100 flex gap-2 justify-end">
        <button className="btn-secondary" onClick={onClose}>Cancel</button>
        <button className="btn-primary" disabled={loading || !form.name || (!isEdit && !form.code)}
          onClick={() => onSubmit(isEdit
            ? { name: form.name, email: form.email || undefined, phone: form.phone || undefined, credit_limit: form.credit_limit }
            : { code: form.code, name: form.name, type: form.type, email: form.email || undefined, phone: form.phone || undefined, credit_limit: form.credit_limit })}>
          {loading ? (isEdit ? 'Saving…' : 'Creating…') : (isEdit ? 'Save changes' : 'Create customer')}
        </button>
      </div>
    </Modal>
  );
}

function KpiTile({ label, value, accent = 'default' }: { label: string; value: number|string; accent?: 'default'|'sage'|'amber'|'danger' }) {
  const bar = { default:'bg-ink-200', sage:'bg-sage-500', amber:'bg-amber-500', danger:'bg-danger' }[accent];
  return (
    <div className="card p-5 relative overflow-hidden">
      <div className={`absolute top-0 left-0 right-0 h-0.5 ${bar}`} />
      <div className="eyebrow text-ink-500">{label}</div>
      <div className="mt-3 text-3xl font-bold font-display text-ink-900 tabular-nums">{typeof value === 'number' ? value.toLocaleString() : value}</div>
    </div>
  );
}

const ip = { width:16,height:16,viewBox:'0 0 24 24',fill:'none',stroke:'currentColor',strokeWidth:2,strokeLinecap:'round' as const,strokeLinejoin:'round' as const };
function PlusIcon() { return <svg {...ip}><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>; }
function ExportIcon() { return <svg {...ip}><path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/></svg>; }
function SearchIcon() { return <svg {...ip} className="text-ink-400 flex-shrink-0"><circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/></svg>; }
function ChevronIcon() { return <svg {...ip} width="14" height="14"><polyline points="9 18 15 12 9 6"/></svg>; }
